import React, { cloneElement, ReactElement, useEffect, useState } from 'react';
import { ColumnComponent } from './Column';
import { Col, Collapse, Row } from 'react-bootstrap';
import Button from 'components/Elements/Button';
import { Icon } from 'components/Icon';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import chunk from 'helpers/chunk';
import { Column, Header, Row as RowType } from 'types/Table';

interface CardItem {
    columns?: Column[];
    rowLink?: () => void | string;
    rowDetails?: React.ReactNode;
}

interface ItemProps {
    card: CardItem;
    card_index: number;
    hasActions?: boolean;
    withImg?: boolean;
    rowClicked?: boolean | ((rowLink: RowType['rowLink']) => void);
    className?: string;
    headers?: (Header | string)[];
}

export const Item = ({
    card,
    card_index,
    hasActions = true,
    withImg = false,
    rowClicked,
    className = '',
    headers = [],
}: ItemProps) => {
    const [open, setOpen] = useState(false);
    const [actions, setActions] = useState<Column | null>(null);
    const [columns, setColumns] = useState<Column[]>(
        card?.columns?.filter(d => !!d) || [],
    );
    const [img, setImg] = useState<Column[] | null>(null);

    const specialLowercase = (the_string: string): string =>
        the_string.toLowerCase().replace(/ id/, ' ID');

    const defaultLinkClicked = (link: (() => void) | string | undefined): void => {
        if (typeof link === 'function') {
            link();
        }
    };

    const buildTableProps = (i: number, rowClick: () => void) => {
        const headerIndex = withImg ? i + 1 : i;
        const headerData = headers[headerIndex] ?? '';

        const isHeaderObject = typeof headerData === 'object';
        const title = isHeaderObject ? (headerData.body ?? '') : headerData;
        const titleClass = isHeaderObject ? (headerData.className ?? '') : '';

        const titleHtml = title ? (
            <div className={`title ${titleClass}`.trim()} onClick={rowClick}>
                {specialLowercase(title as string)}:
            </div>
        ) : null;

        const cardBodyTitleClass = title
            ? 'card-body-row'
            : 'card-body-row card-no-header';

        return { titleHtml, cardBodyTitleClass };
    };

    const findCardTitle = (): [string, number] => {
        let foundTitle: string | boolean = '';
        let cardTitleIndex = -1;

        columns?.forEach((column, i) => {
            if (typeof column === 'object' && 'cardTitle' in column && column.cardTitle) {
                let header = headers[i] ?? '';
                foundTitle = typeof header === 'object' ? header.body ?? '' : header;
                cardTitleIndex = i;
                foundTitle += (foundTitle ? ': ' : '') + column.cardTitle;
            }
        });

        return [foundTitle, cardTitleIndex];
    };

    const [cardTitle, cardTitleIndex] = findCardTitle();

    const filteredColumns = columns.flatMap((column, i) => {
        if (typeof column === 'object' && 'cardHidden' in column && column.cardHidden) return [];
        if (i === cardTitleIndex) return [];

        return [{ ...column, originalIndex: i }];
    });

    const chunks = chunk(filteredColumns, Math.ceil(filteredColumns.length / 2));

    const handleClick = (): void => {
        if (typeof rowClicked === 'function') {
            rowClicked(card.rowLink);
        } else {
            defaultLinkClicked(card.rowLink);
        }
    };

    useEffect(() => {
        let workingColumns = [...(card?.columns?.filter(d => !!d) || [])];

        if (withImg && workingColumns.length > 0) {
            const imageColumn = workingColumns.shift();
            setImg([imageColumn]);
        } else {
            setImg(null);
        }

        if (hasActions && workingColumns.length > 0) {
            const newActions = workingColumns.pop();
            setActions(newActions);
        } else {
            setActions(null);
        }

        setColumns(workingColumns);
    }, [hasActions, withImg, card.columns]);

    return (
        <div
            key={`card-${card_index}`}
            className={`card ${className} xlCards`}
        >
            {cardTitle && (
                <div className="card-title" onClick={handleClick}>
                    {specialLowercase(cardTitle)}
                </div>
            )}
            <div className="card-body">
                {img && cloneElement(img[0].body as ReactElement, {
                    has_ribbon: false,
                    needs_bottom: true,
                })}

                <Row className="card-row flex-1">
                    <Col sm={img ? (actions ? 5 : 6) : (actions ? 4 : 6)}>
                        <ColumnComponent
                            {...{
                                cardTitleIndex,
                                buildTableProps,
                                items: chunks[0] || [],
                                onClick: handleClick,
                            }}
                        />
                    </Col>

                    <Col sm={img ? (actions ? 4 : 6) : (actions ? 4 : 6)}>
                        <ColumnComponent
                            {...{
                                cardTitleIndex,
                                buildTableProps,
                                items: chunks[1] || [],
                                onClick: handleClick,
                            }}
                        />
                    </Col>

                    {actions && (
                        <Col sm={img ? 3 : 4} className={img ? 'actions-menu' : ''}>
                            {typeof actions === 'object' && 'body' in actions
                                ? actions.body
                                : actions as React.ReactNode}
                        </Col>
                    )}
                </Row>
            </div>
            {card.rowDetails && (
                <Col className="d-flex justify-content-center">
                    <Button
                        variant="dark"
                        className="btn-default-size"
                        onClick={() => setOpen(!open)}
                    >
                        <Icon size="1x" icon={open ? faChevronUp : faChevronDown}/>
                    </Button>
                </Col>
            )}
            {card.rowDetails && (
                <Collapse in={open} timeout={0}>
                    <tr className="row-details">
                        <td colSpan={100} className="padding0">
                            {card.rowDetails}
                        </td>
                    </tr>
                </Collapse>
            )}
        </div>
    );
};
